import React, { useState } from 'react'; 
import { useAuth } from "../../context/AuthProvider/useAuth";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import './CommandBar.css';
import { TranslatorS } from '../../components/I18n';
import { Key, Logout, ManageAccounts, PlayArrow, Translate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PaperLayer } from './components/PaperLayer';
import { OuterMenuListing } from './components/OutMenuListing';
import { InnerMenuListing } from './components/InnerMenuListing';
import { OuterMenuWrapper } from './components/OuterMenuWrapper';
import { InnerMenuWrapper } from './components/InnerMenuWrapper';
import { VersionMenuItem } from './components/VersionMenuItem';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AvatarStyleTypeBuilder, UserPicture } from '../UserPicture/UserPicture';
import { useProfile } from '../../context/ProfileProvider/useProfile';
import KeydriveTooltip from '../KeydriveComponents/Tooltip/KeydriveTooltip';
import { KeyDriveChip } from '../KeydriveComponents/Tags/KeyDriveChip';
import { verifyUserGuest } from '../../utils/VerifyUserGuest';
import { KeyDriveBodyMin } from '../KeydriveComponents/Fonts/Body/KeyDriveBodyMin';
import PlanKey from '../../assets/icons/PlanKey';

export type OuterMenuItemTypes = {
  criteria: boolean;
  openSubMenu?: boolean;
  text: string;
  onClick: (e: any) => void;
  icon: JSX.Element;
  optionalIcon?: JSX.Element;
}

export type InnerMenuItemTypes = {
  languageText: string;
  languageValue: string;
}

interface CommandBarProps {
  loadSignture: boolean;
}

const CommandBar: React.FC<CommandBarProps> = ({ loadSignture }) => {

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElLanguage, setAnchorElLanguage] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openLanguage = Boolean(anchorElLanguage);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClickLanguage = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElLanguage(event.currentTarget);
  };
  const handleCloseLanguage = () => {
    setAnchorElLanguage(null);
  };
  const ITEM_HEIGHT = 48;
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useProfile();
  const { nameOrg } = useParams();

  const logout = (event: any) => {
    event.preventDefault();

    auth.logout();
    window.history.replaceState(null, '', '/login');
    if (!auth.token) {
      return <Navigate to="/login" replace />;
    }
    return true;
  };

  const outerMenuItems: OuterMenuItemTypes[] = [
    {
      criteria: false,
      text: t("MyData.Mydata"),
      onClick: () => {navigate(`mydata`); handleClose()},
      icon: <ManageAccounts />
    },
    {
      criteria: false,
      openSubMenu: openLanguage,
      text: t("MyData.Language"),
      onClick: handleClickLanguage,
      icon: <Translate />,
      optionalIcon: <PlayArrow />
    },
    {
      criteria: false,
      text: TranslatorS({path:"Sair.message"}),
      onClick: logout,
      icon: <Logout />
    },
  ];

  const innerMenuItems: InnerMenuItemTypes[] = [
    {
      languageText: "English (EN)",
      languageValue: "en-US"
    },
    {
      languageText: "Espanõl (ES)",
      languageValue: "es"
    },
    {
      languageText: "Português (BR)",
      languageValue: "pt-BR"
    }
    
  ];

  return (
      <>
        <PaperLayer 
          open={open}
          handleClick={handleClick}
        />

        <OuterMenuWrapper
          anchorEl={anchorEl}
          handleClose={handleClose}
          height={ITEM_HEIGHT}
          open={open}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            padding="8px"
          >
            <UserPicture 
                style={AvatarStyleTypeBuilder.innerMenu}
                source={profile.userPicture == undefined || profile.userPicture == "" ? profile.picture : profile.userPicture}
                username={profile?.username}
            />
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  marginTop="8px"
                >
                  <KeyDriveBodyMin 
                    color='#2D2D2D'
                    needBold={false}
                    needEllipses={true}
                    text={profile?.name!}
                  />
                </Box>
                <Typography
                    textAlign="left"
                    color="#2D2D2D"
                    fontSize="12px"
                    sx={{ opacity: .5 }}
                >
                  { profile?.login }
                </Typography>
            </Box>
          </Box>
          { nameOrg && (
            <Box display="flex" justifyContent="center">
              { loadSignture ? (
                <>
                  <CircularProgress variant='indeterminate' />
                </>
              ) : (
                <Box
                  width="calc(100% - 30px)"
                  height="32px"
                  sx={{
                    border: "0.5px solid #22638D33",
                    background: "linear-gradient(180deg, #E9EFF4 0%, rgba(233, 239, 244, 0) 100%)",
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                    justifyContent: "center"
                  }}
                >
                  <PlanKey size={16} color="#22638D"/>
                  <Box marginLeft="4px">
                    <KeyDriveBodyMin 
                      text={`${verifyUserGuest(profile) ? t("Common.Guest") : `${t("AccountAndSubscription.Plan")} ${profile?.signtureInformation?.planName!}`}`}
                      color='#22638D'
                      needBold
                      needEllipses={false}
                    />
                  </Box>
                </Box>
              ) }
            </Box>
          ) }
          <OuterMenuListing
            outerMenuItems={outerMenuItems}
          />
          <VersionMenuItem />
          <InnerMenuWrapper
            anchorEl={anchorElLanguage}
            handleClose={handleCloseLanguage}
            height={ITEM_HEIGHT}
            open={openLanguage}
          >
            <InnerMenuListing 
              handleClose={()=> {handleClose();handleCloseLanguage()}}
              innerMenuItems={innerMenuItems}
            />
          </InnerMenuWrapper>
        </OuterMenuWrapper>
      </>
  );
}

export default CommandBar;
